import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

export interface Card {
    borderPosition?: 'top' | 'left';
    borderColor?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning';
    children?: React.ReactNode;
    delay?: number;
    primary?: boolean;
    className?: string;
}


export interface CardWrapper extends Card {
    animated: boolean;
}

/** 
 * The `Card` is meant to hold content and actions around a certain subject. Use this component for things like dashboard widgets and status widgets.
 * It automatically wraps the content in a `.card-body` and by default, the border will be positioned at the top of the card.  
 * 
 * Aside from the `.card-body`, you can use any of the inner card components provided by https://reactstrap.github.io/components/card/
 */


export const Card = ({ borderPosition, borderColor, children, delay, primary, className }: Card) => {
    const [isAnimated, toggleAnimation] = useState(false);
    useEffect(() => {
        setTimeout(() => toggleAnimation(true), delay);
    }, [delay])
    return (
        <CardWrapper
            borderPosition={borderPosition}
            borderColor={borderColor}
            delay={delay}
            animated={isAnimated}
            primary={primary}
            className={className}
        >
            <CardBody borderPosition={borderPosition} primary={primary}>{children}</CardBody>
        </CardWrapper>
    );
};

Card.defaultProps = {
    borderPosition: 'top',
    borderColor: 'primary',
    delay: 0,
}

export default Card;

const CardBody = styled.div.attrs(({ borderPosition, primary }: Card) => {
const classes = ['card-body', 'border', `${primary ? 'border-primary' : ''}`];
    if (borderPosition === 'top') {
        classes.push('border-top-0', 'rounded-bottom');
    } else {
        classes.push('border-left-0', 'rounded-right');
    }
    return {
        className: classes.join(' '),
    }
})<Card>``;


const animatedCss = css`
    opacity: 1;
    transform: translateY(0);
`;

const CardWrapper = styled.div.attrs(({borderColor, borderPosition, primary, className}: Card) => {
    const classes = [
        className,
        'card', 'border-right-0', 'border-bottom-0',
        `border-${!primary ? borderColor: 'warning'}`,
        `${primary ? 'text-white' : ''}`
    ];
    if (borderPosition === 'top')  classes.push('border-left-0');
    else classes.push('border-top-0');
    if (primary) classes.push('bg-primary');
    return { className: classes.join(' ').trim() }
})<CardWrapper>`
    border-radius: 0;
    border-width: 5px;
    opacity: 0;
    transform: translateY(50px);
    transition: 300ms all cubic-bezier(0.4, 0.0, 0.2, 1);
    ${props => props.animated && animatedCss};
`;